import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Cookie = {
  name: string;
  provider: string;
  duration: string;
  purposeKey: string;
};

type Category = {
  key: "essential" | "analytics";
  cookies: Cookie[];
};

const CATEGORIES: Category[] = [
  {
    key: "essential",
    cookies: [
      {
        name: "photosynthetic_cookie_consent",
        provider: "photosynthetic.com",
        duration: "cookieList.duration.persistent",
        purposeKey: "cookieList.purpose.consent",
      },
      {
        name: "i18nextLng",
        provider: "photosynthetic.com",
        duration: "cookieList.duration.persistent",
        purposeKey: "cookieList.purpose.language",
      },
    ],
  },
  {
    key: "analytics",
    cookies: [
      {
        name: "_ga, _ga_*",
        provider: "Google Analytics",
        duration: "cookieList.duration.years2",
        purposeKey: "cookieList.purpose.ga",
      },
      {
        name: "_gid",
        provider: "Google Analytics",
        duration: "cookieList.duration.hours24",
        purposeKey: "cookieList.purpose.gid",
      },
      {
        name: "_clck",
        provider: "Microsoft Clarity",
        duration: "cookieList.duration.year1",
        purposeKey: "cookieList.purpose.clck",
      },
      {
        name: "_clsk",
        provider: "Microsoft Clarity",
        duration: "cookieList.duration.day1",
        purposeKey: "cookieList.purpose.clsk",
      },
      {
        name: "MUID",
        provider: "Microsoft",
        duration: "cookieList.duration.year1",
        purposeKey: "cookieList.purpose.muid",
      },
      {
        name: "photosynthetic_clarity_visitor_id",
        provider: "photosynthetic.com",
        duration: "cookieList.duration.persistent",
        purposeKey: "cookieList.purpose.clarityVisitor",
      },
      {
        name: "photosynthetic_clarity_session_id",
        provider: "photosynthetic.com",
        duration: "cookieList.duration.session",
        purposeKey: "cookieList.purpose.claritySession",
      },
    ],
  },
];

export const CookieListDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("cookielist:open", handler);
    return () => window.removeEventListener("cookielist:open", handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("cookieList.title")}</DialogTitle>
          <DialogDescription>{t("cookieList.subtitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-2">
          {CATEGORIES.map((cat) => (
            <section key={cat.key}>
              <h3 className="text-sm font-semibold text-foreground mb-1">
                {t(`cookieList.category.${cat.key}.title`)}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {t(`cookieList.category.${cat.key}.desc`)}
              </p>
              <ul className="divide-y divide-border rounded-md border border-border">
                {cat.cookies.map((c) => (
                  <li key={c.name} className="p-3 text-xs">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <span className="font-mono text-[11px] text-foreground break-all">
                        {c.name}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {c.provider} · {t(c.duration)}
                      </span>
                    </div>
                    <p className="mt-1.5 leading-relaxed text-muted-foreground">
                      {t(c.purposeKey)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CookieListDialog;